import React from "react";
import { useSelector } from 'react-redux';
import { Card, CardContent } from "../../ui/card";
import { Loader, Laptop, BatteryCharging } from "lucide-react";
import { getFullOSName } from '../../../utils/misc/system';

const DeviceStatus = () => {
  const { device, deviceLoading } = useSelector(state => state.UserStatus);

  const batteryStatus = (value) => {
    switch (value) {
      case 0:
        return <span className="text-poor">Poor</span>;
      case 1:
        return <span className="text-good">Good</span>;
      default:
        return <span className="text-custom">Plugged In</span>;
    }
  };

  const toGB = (byte, isRam = false) => {
    const k = isRam ? 1000 : 1024;

    if (!byte) {
      return 0;
    }

    return Number((byte / (k * k * k)).toFixed(2));
  }

  const diffGB = (total, used) => {
    return Number((total - used).toFixed(2));
  }

  const usedPercentage = (used, total) => {
    return Number(((used * 100) / total).toFixed(2))
  }

  const cpuPercentage = device?.cpu_usage;;

  const totalDiskSpace = toGB(device?.disk_storage_total);
  const usedDiskSpace = toGB(device?.disk_storage_used);
  const freeDiskSpace = diffGB(totalDiskSpace, usedDiskSpace);
  const usedDiskPercentage = usedPercentage(usedDiskSpace, totalDiskSpace);

  const totalRam = toGB(device?.ram_storage_total, true);
  const usedRam = toGB(device?.ram_storage_used, true);
  const usedRamPercentage = usedPercentage(usedRam, totalRam);

  const getUtilizationColor = (percentage) => {
    if (!percentage) return "bg-gray-500";
    if (percentage >= 80) return "bg-red-500";
    if (percentage >= 50) return "bg-amber-500";
    return "bg-green-500";
  };

  const getPercentageText = (percentage) => percentage ? `${percentage}%` : '';

  const getDiskSpaceText = () => {
    if (!usedDiskSpace || !totalDiskSpace) {
      return;
    }

    return `${usedDiskSpace}GB / ${totalDiskSpace}GB`;
  }

  const getFreeDiskSpaceText = () => {
    if (!freeDiskSpace) {
      return;
    }

    return `${freeDiskSpace}GB free`;
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          {deviceLoading && (<Loader className="h-5 w-5 text-indigo-500 animate-spin" />)}
          {!deviceLoading && (<Laptop className="h-5 w-5 text-indigo-500" />)}
          <h3 className="font-semibold text-lg">Device Status</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">Hardware and operating system information for this device</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Battery Health</p>
              <div className="flex items-center gap-1">
                <BatteryCharging className="h-4 w-4 text-green-500" />
                <p className="font-semibold">{batteryStatus(device?.avg_battery_health)}</p>
              </div>
            </div>
            {device?.serial_number && (<div>
              <p className="text-sm text-gray-500">Serial Number</p>
              <p className="font-semibold text-xs">{device.serial_number}</p>
            </div>)}
          </div>

          {/* CPU Utilization - now matching memory and disk space style */}
          <div>
            <p className="text-sm text-gray-500">CPU Utilization</p>
            <p className="font-semibold">{getPercentageText(cpuPercentage)}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div className={`${getUtilizationColor(cpuPercentage)} h-2 rounded-full`} style={{ width: `${cpuPercentage}%` }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500">Memory Utilization</p>
            <p className="font-semibold text-yellow-500">{getPercentageText(usedRamPercentage)}</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              {!!usedRamPercentage && (<div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${usedRamPercentage}%` }}></div>)}
              {!usedRamPercentage && (<div className="bg-gray-500 h-2 rounded-full" style={{ width: `100%` }}></div>)}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500">Disk Space</p>
            <div className="flex items-center justify-between">
              <p className="font-semibold">{getDiskSpaceText()}</p>
              <p className="text-xs">{getFreeDiskSpaceText()}</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div className={`${getUtilizationColor(usedDiskPercentage)} h-2 rounded-full`} style={{ width: `${usedDiskPercentage}%` }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Operating System</p>
              <p className="font-semibold">{getFullOSName(device?.operating_system) || 'N/A'}</p>
              {device?.os_version && <p className="text-xs text-gray-500">Version: {device?.os_version}</p>}
            </div>
            <div>
              <p className="text-sm text-gray-500">CPU</p>
              <p className="font-semibold">{device?.cpu_info || 'N/A'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Chrome Version</p>
              <p className="font-semibold">{device?.chrome_version || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Update</p>
              <p className="font-semibold">{device?.monitored_at || 'N/A'}</p>
            </div>
          </div>

          {/* Most Recent User */}
          <div>
            <p className="text-sm text-gray-500">Most Recent User</p>
            <div className="flex items-center gap-1">
              <p className="font-semibold">{device?.recent_users?.[0] || 'N/A'}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DeviceStatus;
